import {
  cancelGrantVestmentRequestAPI,
  getGrantVestmentAPI,
  getGrantVestmentRequestAPI,
  getGrantVestmentRequestsByFundAPI,
  GetGrantVestmentRequestsQuery,
  getGrantVestmentsAPI,
  getGrantVestmentsQuery,
  makeGrantVestmentRequestAPI,
  addGrantvestmentInteractionAPI,
  GrantvestmentInteractionInput,
  GetGrantvestmentInteraction,
  getGrantvestmentInteractionAPI,
  deleteGrantvestmentInteractionAPI,
  addGrantvestmentFollowerAPI,
  deleteGrantvestmentFollowerAPI,
  getGrantvestmentFollowerAPI,
  GrantvestmentFollowerInput,
  getGrantVestmentDistributionByFundAPI,
  GetGrantVestmentDistributionQuery,
} from "api/grant-vestment.api";
import { useInfiniteQuery, useMutation, useQuery } from "react-query";
import { FundGvDistribution } from "types/fund-gv-distribution";
import {
  GrantVestment,
  GrantVestmentRequest,
  LegalComplianceChecklist,
} from "types/grant-vestment";
import { GrantVestmentFollower } from "types/grant-vestment-follower";
import { GrantVestmentInteraction } from "types/grant-vestment-interaction";
import { PaginatedEntityList } from "types/pagination";

const GRANT_VESTMENT_RQ_KEY = "grant-vestments";
const GRANT_VESTMENT_REQUEST_RQ_KEY = "grant-vestment-request";
const GRANT_VESTMENT_INTERACTION_RQ_KEY = "grant-vestment-interaction";
const GRANT_VESTMENT_FOLLOWER_RQ_KEY = "grant-vestment-follower";
const GRANT_VESTMENT_DISTRIBUTION_RQ_KEY = "grant-vestment-distribution";

export const useGetGrantVestmentQuery = ({
  grantVestmentId,
}: {
  grantVestmentId: number;
}) => {
  return useQuery<GrantVestment>({
    queryKey: [GRANT_VESTMENT_RQ_KEY, grantVestmentId],
    enabled: grantVestmentId !== 0,
    queryFn: () => getGrantVestmentAPI(grantVestmentId),
  });
};
export const useGetGrantVestmentsQuery = (query: getGrantVestmentsQuery) => {
  return useQuery<PaginatedEntityList<GrantVestment>>({
    queryKey: [GRANT_VESTMENT_RQ_KEY, query],
    queryFn: () => getGrantVestmentsAPI(query),
  });
};
export const useGetGrantVestmentsQueryWithPagination = (
  query: getGrantVestmentsQuery
) => {
  return useInfiniteQuery<PaginatedEntityList<GrantVestment>>({
    queryKey: [GRANT_VESTMENT_RQ_KEY, query],
    queryFn: ({ pageParam = 0 }) =>
      getGrantVestmentsAPI({ ...query, offset: pageParam }),
    getNextPageParam: (lastPage) => {
      return lastPage.pagination.nextOffset;
    },
  });
};
export const useMakeGrantVestmentRequestMutation = (
  grantVestmentId: number
) => {
  return useMutation<
    unknown,
    unknown,
    {
      requestedAmount: number;
      fundId: number;
      transactionType: string;
      investmentInfo?: {
        requestedAmount: number;
        appliedAmount: number;
        interestedIn: string;
        units: number;
        ticker: string;
      };
      emailToNotify?: string;
      legalComplianceChecklist?: LegalComplianceChecklist;
    }
  >({
    mutationKey: [GRANT_VESTMENT_RQ_KEY, grantVestmentId],
    mutationFn: (data) => makeGrantVestmentRequestAPI(grantVestmentId, data),
  });
};
export const useCancelGrantVestmentRequestMutation = (
  grantVestmentId: number
) => {
  return useMutation<unknown, unknown, unknown>({
    mutationKey: [GRANT_VESTMENT_RQ_KEY, grantVestmentId],
    mutationFn: () => cancelGrantVestmentRequestAPI(grantVestmentId),
  });
};

export const useGetGrantVestmentRequestQuery = (
  grantVestmentRequestId: number
) => {
  return useQuery<GrantVestmentRequest>({
    queryKey: [GRANT_VESTMENT_RQ_KEY, grantVestmentRequestId],
    enabled: grantVestmentRequestId !== 0,
    queryFn: () => getGrantVestmentRequestAPI(grantVestmentRequestId),
  });
};

export const useGetGrantVestmentRequestsQuery = (
  grantVestmentId: number,
  query: GetGrantVestmentRequestsQuery
) => {
  return useInfiniteQuery<PaginatedEntityList<GrantVestmentRequest>>({
    queryKey: [GRANT_VESTMENT_REQUEST_RQ_KEY, grantVestmentId],
    queryFn: ({ pageParam = 0 }) =>
      getGrantVestmentRequestsByFundAPI(grantVestmentId, {
        ...query,
        offset: pageParam,
      }),
    getNextPageParam: (lastPage) => {
      return lastPage.pagination.nextOffset;
    },
  });
};

export const useGetGrantVestmentDistributionQuery = (
  query: GetGrantVestmentDistributionQuery
) => {
  return useInfiniteQuery<PaginatedEntityList<FundGvDistribution>>({
    queryKey: [GRANT_VESTMENT_DISTRIBUTION_RQ_KEY],
    queryFn: ({ pageParam = 0 }) =>
      getGrantVestmentDistributionByFundAPI({
        ...query,
        offset: pageParam,
      }),
    getNextPageParam: (lastPage) => {
      return lastPage.pagination.nextOffset;
    },
  });
};

export const useCreateGrantVestmentInteractionMutation = () => {
  return useMutation<unknown, unknown, GrantvestmentInteractionInput>({
    mutationKey: [GRANT_VESTMENT_INTERACTION_RQ_KEY],
    mutationFn: (grantvestmentInteractionInput) =>
      addGrantvestmentInteractionAPI(grantvestmentInteractionInput),
  });
};

export const useGetGrantVestmentInteractionQuery = (
  query: GetGrantvestmentInteraction
) => {
  return useInfiniteQuery<PaginatedEntityList<GrantVestmentInteraction>>({
    queryKey: [GRANT_VESTMENT_INTERACTION_RQ_KEY, query],
    queryFn: ({ pageParam = 0 }) =>
      getGrantvestmentInteractionAPI({ ...query, offset: pageParam }),
    getNextPageParam: (lastPage) => {
      return lastPage.pagination.nextOffset;
    },
  });
};

export const useGetGrantVestmentFollowerQuery = (
  query: GetGrantvestmentInteraction
) => {
  return useInfiniteQuery<PaginatedEntityList<GrantVestmentFollower>>({
    queryKey: [GRANT_VESTMENT_FOLLOWER_RQ_KEY, query],
    queryFn: ({ pageParam = 0 }) =>
      getGrantvestmentFollowerAPI({ ...query, offset: pageParam }),
    getNextPageParam: (lastPage) => {
      return lastPage.pagination.nextOffset;
    },
  });
};

export const useDeleteGrantVestmentInteractionMutation = () => {
  return useMutation({
    mutationKey: [GRANT_VESTMENT_INTERACTION_RQ_KEY],
    mutationFn: (interactionId: number) =>
      deleteGrantvestmentInteractionAPI(interactionId),
  });
};

export const useDeleteGrantVestmentFollowerMutation = () => {
  return useMutation({
    mutationKey: [GRANT_VESTMENT_FOLLOWER_RQ_KEY],
    mutationFn: (followerId: number) =>
      deleteGrantvestmentFollowerAPI(followerId),
  });
};

export const useCreateGrantVestmentFollowerMutation = () => {
  return useMutation<unknown, unknown, GrantvestmentFollowerInput>({
    mutationKey: [GRANT_VESTMENT_FOLLOWER_RQ_KEY],
    mutationFn: (grantvestmentFollowerInput) =>
      addGrantvestmentFollowerAPI(grantvestmentFollowerInput),
  });
};
